import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import { VerificationService,
         FirstResponderPersonalInfoViewModel,
         FieldValidationErrors,
         ExtendedFieldValidationErrors,
         Organization,
         FormSelectChoice,
         FirstResponderPersonalInfoResponse,
         InputSelectOnKeyDown,
         isFormFilled,
         FormFooterComponent,
         FirstResponderStatusComponent,
         updateFieldValidationErrorsByFieldId,
         updateViewModelOrganization,
         submitForm,
         handleEmailOnKeyDown,
         setRef,
         FirstNameComponent,
         LastNameComponent,
         EmailComponent,
         FirstResponderOrganizationComponent,
         FirstResponderStatusDefaultMessagesEnum,
         Metadata,
         setFocus,
         VerificationStepsEnum,
         PostalCodeComponent,
} from '@sheerid/jslib';
import { getSafe } from '../lib/utils/objects';
import { BirthDate } from './BirthDate2Component';
import { LogoHeaderComponent } from './LogoHeaderComponent';



interface StepFirstResponderPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

export const StepFirstResponderPersonalInfo = ({ intl, verificationService }: StepFirstResponderPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as FirstResponderPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;

    const updateFirstResponderViewModel = (key: keyof FirstResponderPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: FirstResponderPersonalInfoViewModel = produce(viewModel, (draft: FirstResponderPersonalInfoViewModel) => {
            // @ts-ignore
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const collectPostalCode = getSafe(() => verificationService.programTheme.openOrgSearchEnabled)
        && getSafe(() => viewModel.organization.id) === 0
        && getSafe(() => viewModel.organization.name, '').length > 0;

    if (collectPostalCode) {
        viewModel.postalCode = (viewModel.postalCode ? viewModel.postalCode : '');
    } else {
        if (viewModel.postalCode || viewModel.postalCode === '') {
            delete viewModel.postalCode;
            verificationService.fieldValidationErrors.postalCode = undefined;
        }
    }


    const getAvailableFirstResponderStatuses = ():FormSelectChoice[] => {
        const availableStatusesResponse =
            (verificationService.verificationResponse as FirstResponderPersonalInfoResponse).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: FirstResponderStatusDefaultMessagesEnum[status],
            });
        }
        return availableStatuses;
    };

    const handleFirstResponderStatusOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('organization');
        }
    };

    return (
        <div id="sid-step-active-first-responder-personal-info" className="sid-l-container">
            <div className="sid-header">
                <LogoHeaderComponent/>
                <div className="sid-header__title sid-l-space-top-lg">
                    <h1>
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Teaching Staff-Only Offer" />
                    </h1>
                </div>

                <div className="sid-header__subtitle sid-l-space-top-sm">
                    <FormattedHTMLMessage id="step.personalInfo.subtitle" defaultMessage="Complete the form below to confirm your eligibility." />
                </div>
            </div>

            <div className="sid-l-space-top-lg"></div>

            <FirstResponderStatusComponent
                value={{
                    value: (verificationService.viewModel as FirstResponderPersonalInfoViewModel).status,
                    label: FirstResponderStatusDefaultMessagesEnum[(verificationService.viewModel as FirstResponderPersonalInfoViewModel).status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableFirstResponderStatuses() }
                onChange={
                    (status: FormSelectChoice) => {
                        updateFirstResponderViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                        if (status) {
                            setFocus('organization');
                        }
                    }
                }
                onKeyDown={event => handleFirstResponderStatusOnKeyDown(event)}
            />

            <FirstResponderOrganizationComponent
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as FirstResponderPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstNameComponent
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue) => {
                            updateFirstResponderViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                    />

                <LastNameComponent
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue) => {
                            updateFirstResponderViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            {
                collectPostalCode ?
                <PostalCodeComponent
                    isErrored={!!fieldValidationErrors.postalCode}
                    onChange={(newValue) => {
                        updateFirstResponderViewModel('postalCode', newValue);
                        updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                    }}
                    value={viewModel.postalCode}
                />
                :
                null
            }

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation= {' '}
                onChange={
                    (newValue) => {
                        updateFirstResponderViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateFirstResponderViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
                verificationService={verificationService}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <button
                    id="sid-submit-btn-collect-info"
                    onClick={() => submitForm(viewModel, verificationService, "collectFirstResponderPersonalInfo" as VerificationStepsEnum)}
                    type="submit"
                    className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                    aria-label="submit"
                    ref={button => setRef('submitButton', button)}
                >
                    <FormattedHTMLMessage id="verifyMyFirstResponderStatus" defaultMessage="SUBMIT FOR REVIEW" />
                </button>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="firstResponderInfoShared"
                    defaultMessage={'Information entered here will be used for verification purposes. The information will be shared with Purple.'}
                />
            </FormFooterComponent>
        </div>
    );
};

export const StepFirstResponderPersonalInfoComponent = injectIntl(StepFirstResponderPersonalInfo);
